// @flow
// $FlowIssue need to update to a more recent flow version
import React, { useEffect } from 'react';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import type { Dispatch } from 'redux';
import type { Connector } from 'react-redux';
import SubmitButton from '../../components/SubmitButton';
import * as dialog from '../../ducks/dialog';
import { actions } from './duck';
import styles from './styles';
import type { State as RootState } from '../../redux/initialState';

type OwnProps = {}
type StateProps = {
    customerIds: number[],
    onClose: () => void,
    onSubmitFail: () => void,
    onSubmitSuccess: () => void,
    open: boolean,
};
type DispatchProps = {
    registerDialog: (name: string) => void,
    submit: (values: Object) => Promise<void>,
    unregisterDialog: (name: string) => void,
};
type Props = OwnProps & StateProps & DispatchProps;

const DeleteUsersSchema = yup.object().shape({
    customerIds: yup.array().of(yup.number()),
});

const useStyles = makeStyles(styles, { name: 'DeleteUsersDialog' });

export function DeleteUsersDialog(props: Props) {
    const { customerIds, onClose, onSubmitFail, onSubmitSuccess, open, registerDialog, submit, unregisterDialog } = props;

    const { t } = useTranslation();
    const classes = useStyles(props);

    useEffect(() => {
        registerDialog(DeleteUsersDialog.NAME);
        return () => {
            unregisterDialog(DeleteUsersDialog.NAME);
        };
    }, [registerDialog, unregisterDialog]);

    return (
        <Formik
          enableReinitialize
          initialValues={{ customerIds }}
          isInitialValid
          onSubmit={(values, { setStatus, setSubmitting }) => {
              submit(values)
                  .then(() => {
                      setStatus({ success: true });
                      onSubmitSuccess();
                  })
                  .catch((error) => {
                      setStatus({ error });
                      onSubmitFail();
                  })
                  .then(() => setSubmitting(false));
          }}
          validationSchema={DeleteUsersSchema}
        >
            {(formikProps) => {
                const { isSubmitting, isValid, handleReset, handleSubmit } = formikProps;
                return (
                    <Dialog maxWidth="xs" open={open} onClose={onClose} onExited={handleReset}>
                        <Form onSubmit={handleSubmit}>
                            <DialogTitle>{t('deleteUsersDialog.title')}</DialogTitle>
                            <DialogContent>
                                <DialogContentText>{t('deleteUsersDialog.text')}</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={onClose}>
                                    {t('deleteUsersDialog.cancel')}
                                </Button>
                                <SubmitButton
                                  className={classes.submitButton}
                                  color="primary"
                                  disabled={isSubmitting || !isValid}
                                  submitting={isSubmitting}
                                  type="submit"
                                  variant="contained"
                                >
                                    {t('deleteUsersDialog.confirm')}
                                </SubmitButton>
                            </DialogActions>
                        </Form>
                    </Dialog>
                );
            }}
        </Formik>
    );
}

DeleteUsersDialog.NAME = 'deleteUsers';
DeleteUsersDialog.defaultProps = {
    customerIds: [],
    onClose: () => {},
    onSubmitFail: () => {},
    onSubmitSuccess: () => {},
    open: false,
};

const mapStateToProps = (state: RootState): StateProps => ({
    ...DeleteUsersDialog.defaultProps,
    ...state.dialog.registered[DeleteUsersDialog.NAME],
});
const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    registerDialog: (name: string) => dispatch(dialog.actions.register(name)),
    submit: (values) => dispatch(actions.submit(values)),
    unregisterDialog: (name: string) => dispatch(dialog.actions.unregister(name)),
});

const connector: Connector<OwnProps, Props> = connect(mapStateToProps, mapDispatchToProps);
export default connector(DeleteUsersDialog);
